/**
 * Resolve a caller-supplied post-auth destination to something safe to
 * `router.push`.
 *
 * Only a same-origin absolute path survives: it must start with "/" but not
 * with "//" or "/\" (both of which browsers resolve to ANOTHER origin — the
 * open-redirect vector). Absolute `http(s)://`, `javascript:`, empty or
 * malformed percent-encoded values fall back.
 *
 * One rule for every post-auth redirect. The login page had it as a private
 * helper; the OAuth callback next to it pushed the raw `redirectTo` and was an
 * open redirect on the real dashboard domain (2026-09-10 review).
 */
export function resolveSafeRedirect(
  raw: string | null | undefined,
  fallback = '/dashboard',
): string {
  if (typeof raw !== 'string' || !raw) return fallback
  // A value read with useSearchParams().get() has been decoded once already
  // and reads as a path. Only a producer that encoded the WHOLE path again
  // leaves it starting "%2F" — decode that one. Decoding a path a second time
  // changed the address: `?q=a%26b` became `?q=a&b` (2026-10-06).
  let next = raw
  if (!next.startsWith('/')) {
    try {
      next = decodeURIComponent(raw)
    } catch {
      // Malformed percent-encoding → reject.
      return fallback
    }
  }
  return isSameOriginPath(next) ? next : fallback
}

/** Any origin works for resolving: only whether the path stays on it matters. */
const PROBE_ORIGIN = 'https://same-origin.invalid'

/**
 * Is `value` a path on this site — one that cannot take the browser anywhere
 * else, however the URL parser reads it?
 *
 * Starts with one "/" (not "//", not "/\"); carries no control character —
 * the parser DELETES tab, CR and LF before reading, so `/<TAB>/evil.example`
 * passed the slash test and then resolved to //evil.example (2026-10-06); and
 * resolves to the origin it was resolved against.
 */
export function isSameOriginPath(value: string): boolean {
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return false
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(value)) return false
  try {
    return new URL(value, PROBE_ORIGIN).origin === PROBE_ORIGIN
  } catch {
    return false
  }
}
